import React, { Fragment, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SortableTH } from "../Table/Header/SortableTH";
import strings from "../../i18n/i18nStrings";
import { TDevice } from "../../types/Devices";
import NameCircle from "../nameCircle/NameCircle";
import dayjs from "dayjs";
import { CONFIG } from "../../constants/config";
import DeviceOnlineStateIcon from "./DeviceStateIcon";
import { EyeIcon } from "lucide-react";
import { DefaultFilterType } from "../../types/PageFilterTypes";
import { useDeviceSettingsContext } from "../../context/DeviceSettingsContext";
import { useLocations } from "../../hooks/useLocations";

interface DevicesTableProps {
  devices: TDevice[];
  updateFilter: (filter: DefaultFilterType) => void;
  filters: DefaultFilterType;
  showAddDeviceButton?: boolean;
}

const DevicesTable: React.FC<DevicesTableProps> = ({
  devices,
  updateFilter,
  filters,
  showAddDeviceButton = true,
}) => {
  const navigate = useNavigate();
  const [hoveredDevice, setHoveredDevice] = React.useState<string | null>(null);
  const { onOpenDeviceModal } = useDeviceSettingsContext();
  const { locations } = useLocations();

  // Create a lookup map for location colors
  const locationColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    locations?.forEach(location => {
      if (location._id && location.color) {
        map[location._id] = location.color;
      }
    });
    return map;
  }, [locations]);
  
  const goToDevicePage = (device: TDevice) => {
    navigate(`/devices/${device.device_id}`);
  };
  
  const handleMobileRowHover = (deviceId: string, isHovering: boolean) => {
    setHoveredDevice(isHovering ? deviceId : null);
  };

  const checkDeviceHasError = (device: TDevice): boolean => {
    // Tank status - disconnected or unsealed
    if (device.tank_disconnected === true || device.tank_unsealed === true) {
      return true;
    }
    
    // Battery level is low (assuming "Good" means above a certain threshold)
    // You may need to adjust this threshold based on what constitutes a "Good" battery level
    if (device.battery_percentage !== undefined && device.battery_percentage < 20) {
      return true;
    }
    
    return false;
  };

  const getTextColor = (backgroundColor: string): string => {
    // Convert hex to RGB
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate brightness
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // Return black text for light backgrounds, white text for dark backgrounds
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  const getLocationColor = (device: TDevice): string => {
    // First try to get color from device.location.color (if API provides it)
    if (device.location?.color) {
      return device.location.color;
    }
    // Fallback to lookup map using location_id
    if (device.location_id && locationColorMap[device.location_id]) {
      return locationColorMap[device.location_id];
    }
    // Final fallback to default blue
    return "#3B82F6";
  };

  const handlePhotosClick = (device: TDevice) => {
    navigate(`/devices/${device.device_id}?openPhotos=true`);
  };


  return (
    <>
      <table className="min-w-full">
      <thead>
        <tr className="white">
          <SortableTH
            sortKey={"name"}
            updateFilter={(o) => updateFilter(o)}
            filters={filters}
            classNames="pl-6"
          >
            {strings.dashboard.tableHeader.devices(devices.length)}
          </SortableTH>

          {/* <SortableTH
            sortKey={"last_watered_at"}
            updateFilter={(o) => updateFilter(o)}
            filters={filters}
            classNames="w-1/12 hidden md:table-cell"
          >
            {strings.dashboard.tableHeader.watered}
          </SortableTH> */}

          <SortableTH
            sortKey={"water_percentage"}
            updateFilter={(o) => updateFilter(o)}
            filters={filters}
            classNames="w-1/12 pl-5 hidden md:table-cell"
          >
            {strings.dashboard.tableHeader.fill}
          </SortableTH>

          <SortableTH
            sortKey={"battery_percentage"}
            updateFilter={(o) => updateFilter(o)}
            filters={filters}
            classNames="w-1/12 pl-5 hidden md:table-cell"
          >
            {strings.dashboard.tableHeader.battery}
          </SortableTH>

          <th className="w-2/12 px-.5 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500 hidden md:table-cell">
            {strings.dashboard.tableHeader.location}
          </th>
          <SortableTH
            sortKey={"last_event_updated_at"}
            updateFilter={(o) => updateFilter(o)}
            filters={filters}
            classNames="w-1/12 hidden md:table-cell"
          >
            {strings.dashboard.tableHeader.online}
          </SortableTH>
          <th className="hidden md:table-cell w-1/12 px-6 py-2" />
        </tr>
      </thead>
      <tbody>
        {devices?.length > 0
          ? devices.map((device, idx) => (
              <Fragment key={device.device_id}>
                {/* If the device never called in, show an info row */}
                {!device.last_event_updated_at && (
                  <tr className="group hover:bg-blue-50 bg-gray-200 transition-colors duration-250 ease-in-out border-b-2 border-solid md:border-none">
                    <td
                      className="w-3/12 px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => goToDevicePage(device)}
                    >
                      <div className="flex items-center">
                        <NameCircle 
                          name={device.name} 
                          color={device.color} 
                          hasError={checkDeviceHasError(device)} 
                          hasPhotos={device.photos && device.photos.length > 0}
                          onPhotosClick={() => handlePhotosClick(device)}
                        />
                        <div className="ml-3">
                          <div className="text-sm font-semibold text-gray-900">
                            {device.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      colSpan={6}
                      className="px-6 py-4 whitespace-nowrap hidden md:table-cell"
                    >
                      {!device.created_at ||
                      dayjs(dayjs()).diff(device.created_at) >=
                        CONFIG.MINUTES_FOR_DEVICE_TO_CALL_IN * 60 * 1000 ? (
                        <div className="text-xs font-semibold text-red-600">
                          Device was unable to connect. Please ensure it's fully
                          charged and turned on.
                          <div>
                            <small>
                              Device created:{" "}
                              {device.created_at
                                ? dayjs(device.created_at).format(
                                    CONFIG.STANDARD_DATETIME_FORMAT
                                  )
                                : "No date given"}
                            </small>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs font-semibold text-blue-600">
                          Adding Device. Please ensure the device is turned on
                          and the LED is blinking. Allow up to five minutes.
                          <div>
                            <small>
                              {dayjs(device.created_at).format(
                                CONFIG.STANDARD_DATETIME_FORMAT
                              )}
                            </small>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                {/* If the device has called in, data */}
                {device.last_event_updated_at && (
                  <>
                    {/* Desktop row */}
                    <tr
                      key={device._id}
                      className="hidden md:table-row group hover:bg-blue-50 transition-colors duration-250 ease-in-out border-b-2 border-solid md:border-none"
                    >
                    {/* <td className="px-6 py-2 whitespace-nowrap">
                <input
                  type="checkbox"
                  // checked={!!selectedDevices[device.id] || false}
                  // onClick={(evt) => {
                  //   evt.stopPropagation();
                  //   toggleDeviceSelection(device.id);
                  // }}
                  className="form-checkbox h-4 w-4 text-blue-600"
                />
              </td> */}
                    <td
                      className="w-3/12 px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => goToDevicePage(device)}
                    >
                      <div className="flex items-center">
                        <NameCircle 
                          name={device.name} 
                          color={device.color} 
                          hasError={checkDeviceHasError(device)}
                          hasPhotos={device.photos && device.photos.length > 0}
                          onPhotosClick={() => handlePhotosClick(device)}
                        />
                        <div className="ml-3">
                          <div className="text-sm font-bold text-gray-900">
                            {device.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* <td className="hidden md:table-cell w-1/12 pl-.5 pr-6 py-4 whitespace-nowrap text-xs font-medium text-gray-900">
                      {device.last_watered_at
                        ? new Date(device.last_watered_at).toLocaleDateString()
                        : "N/a"}
                    </td> */}
                    <td className="w-2/12 pl-5 pr-12 py-4 whitespace-nowrap ">
                      {device.tank_disconnected === true ||
                      device.water_percentage === -1 ? (
                        <div>
                          <em className="text-gray-400 text-xs font-bold">
                            Tank Disconnected
                          </em>
                        </div>
                      ) : (
                        <>
                          <div className="text-xs font-medium text-gray-900 mb-1.5">
                            {device.water_percentage?.toFixed(0) || 0}%
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5 text-gray-900 mb-1">
                            <div
                              className="bg-blue-500 h-1.5 rounded-full"
                              style={{
                                width: `${
                                  device.water_percentage?.toFixed(0) || 0
                                }%`,
                              }}
                            ></div>
                          </div>
                          {device.estimated_refill_date && (
                            <div className="text-xs font-semibold text-gray-400">
                              Est. refill {new Date(device.estimated_refill_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', timeZone: 'UTC' })}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="w-2/12 px-.5 py-4 pr-7 pl-5 whitespace-nowrap">
                      <div className="text-xs font-medium text-gray-900 mb-1.5">
                        {device.battery_percentage?.toFixed(0) || 0}%
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 text-gray-900 mb-1">
                        <div
                          className="bg-green-500 h-1.5 rounded-full"
                          style={{
                            width: `${
                              device.battery_percentage?.toFixed(0) || 0
                            }%`,
                          }}
                        ></div>
                      </div>
                      {/* Placeholder div to align with water section's estimated refill date */}
                      <div className="text-xs h-4">
                        {/* Empty space to match estimated refill date height */}
                      </div>
                    </td>
                    {/* <td
                        className="w-3/12 px-6 py-4 whitespace-nowrap cursor-pointer"
                      >
                        <div className="flex items-center">
                          <LocCircle name={device.location.name} />
                          <div className="ml-3">
                            <div className="text-sm font-semibold text-gray-900">
                            {device.location?.name}
                            
                            </div>
                          </div>
                        </div>
                  
                      </td> */}
                    <td className="w-1/12 px-.5 text-center py-4 whitespace-nowrap md:table-cell">
                      {device.location?.name ? (
                        <span
                          className="px-3 pt-1 pb-1 text-xs leading-8 font-semibold rounded-full"
                          style={{
                            backgroundColor: getLocationColor(device),
                            color: getTextColor(getLocationColor(device)),
                            borderColor: getLocationColor(device)
                          }}
                          title={`Location: ${device.location?.name}, Color: ${getLocationColor(device)}`}
                        >
                          {device.location?.name && device.location.name.length > 30 
                            ? `${device.location.name.substring(0, 30)}...` 
                            : device.location?.name}
                        </span>
                      ) : (
                        <button
                          onClick={() => onOpenDeviceModal(device)}
                          className="text-xs font-semibold underline text-blue-700 hover:text-blue-800"
                        >
                          Add Location
                        </button>
                      )}
                    </td>
                    <td className="md:table-cell w-1/12 px-6 py-4 whitespace-nowrap text-sm font-m text-gray-900">
                      <div className="flex justify-center">
                        <DeviceOnlineStateIcon
                          showLabel={false}
                          updated_at={device.last_event_updated_at}
                        />
                      </div>
                    </td>
                    <td
                      className="hidden md:table-cell w-1/12 text-blue-700 cursor-pointer"
                      onClick={() => goToDevicePage(device)}
                    >
                      <EyeIcon className="invisible group-hover:visible" />
                    </td>
                    </tr>
                    
                    {/* Mobile rows */}
                    <tr
                      key={`${device._id}-mobile-1`}
                      className={`md:hidden transition-colors duration-250 ease-in-out cursor-pointer ${
                        hoveredDevice === device.device_id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => goToDevicePage(device)}
                      onMouseEnter={() => handleMobileRowHover(device.device_id, true)}
                      onMouseLeave={() => handleMobileRowHover(device.device_id, false)}
                    >
                      <td colSpan={6} className="px-6 py-6">
                        <div className="flex items-center justify-between">
                          {/* Left side: Circle, Name, Location */}
                          <div className="flex items-center flex-1">
                            <NameCircle 
                              name={device.name} 
                              color={device.color} 
                              hasError={checkDeviceHasError(device)}
                              hasPhotos={device.photos && device.photos.length > 0}
                              onPhotosClick={() => handlePhotosClick(device)}
                            />
                            <div className="ml-3 flex-1">
                              <div className="text-sm font-bold text-gray-900">
                                {device.name.length > 23 ? `${device.name.substring(0, 23)}...` : device.name}
                              </div>
                              <div className="text-xs mt-3">
                                {device.location?.name ? (
                                  <span
                                    className="px-2 py-1 rounded-full font-semibold"
                                    style={{
                                      backgroundColor: getLocationColor(device),
                                      color: getTextColor(getLocationColor(device))
                                    }}
                                    title={`Location: ${device.location?.name}, Color: ${getLocationColor(device)}`}
                                  >
                                    {device.location?.name && device.location.name.length > 30 
                                      ? `${device.location.name.substring(0, 30)}...` 
                                      : device.location?.name}
                                  </span>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onOpenDeviceModal(device);
                                    }}
                                    className="text-xs font-semibold underline text-blue-700 hover:text-blue-800"
                                  >
                                    Add Location
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Right side: Online Status */}
                          <div className="flex items-center">
                            <DeviceOnlineStateIcon
                              showLabel={false}
                              updated_at={device.last_event_updated_at}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Mobile second row - Tank and Battery */}
                    <tr
                      key={`${device._id}-mobile-2`}
                      className={`md:hidden transition-colors duration-250 ease-in-out border-b-2 border-solid cursor-pointer ${
                        hoveredDevice === device.device_id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => goToDevicePage(device)}
                      onMouseEnter={() => handleMobileRowHover(device.device_id, true)}
                      onMouseLeave={() => handleMobileRowHover(device.device_id, false)}
                    >
                      <td colSpan={6} className="px-6 py-3 pt-0 pb-6">
                        <div className="flex justify-between gap-4">
                          {/* Tank Fill */}
                          <div className="flex-1">
                            {/* MOBILE TABLE */}
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                              Cwm water level
                            </div>
                            {device.tank_disconnected === true ||
                            device.water_percentage === -1 ? (
                              <div>
                                <em className="text-gray-400 text-xs font-bold">
                                  Tank Disconnected
                                </em>
                              </div>
                            ) : (
                              <>
                                <div className="text-xs font-medium text-gray-900 mb-1.5">
                                  {device.water_percentage?.toFixed(0) || 0}%
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 text-gray-900 mb-1">
                                  <div
                                    className="bg-blue-500 h-1.5 rounded-full"
                                    style={{
                                      width: `${
                                        device.water_percentage?.toFixed(0) || 0
                                      }%`,
                                    }}
                                  ></div>
                                </div>
                                {device.estimated_refill_date && (
                                  <div className="text-xs font-semibold text-gray-400">
                                    Est. refill {new Date(device.estimated_refill_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', timeZone: 'UTC' })}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          
                          {/* Battery Level */}
                          <div className="flex-1">
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                              Battery
                            </div>
                            <div className="text-xs font-medium text-gray-900 mb-1.5">
                              {device.battery_percentage?.toFixed(0) || 0}%
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 text-gray-900 mb-1">
                              <div
                                className="bg-green-500 h-1.5 rounded-full"
                                style={{
                                  width: `${
                                    device.battery_percentage?.toFixed(0) || 0
                                  }%`,
                                }}
                              ></div>
                            </div>
                            {/* Placeholder div to align with water section's estimated refill date */}
                            <div className="text-xs h-4">
                              {/* Empty space to match estimated refill date height */}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </>
                )}
              </Fragment>
            ))
          : showAddDeviceButton && (
              <Fragment>
                {/* If the device never called in, show an info row */}
                <tr className="group bg-gray-200 transition-colors duration-250 ease-in-out border-b-2 border-solid md:border-none">
                  <td colSpan={12} className="align-middle text-center py-8">
                    <div className="text-gray-600 uppercase text-sm font-bold">
                      {/* {strings.dashboard.emptyTitle} */}
                      No Devices Found or Match Search Criteria
                    </div>
                    <div className="text-gray-600 text-sm font-semibold pt-2">
                      {/* {strings.dashboard.emptyMessage} */}
                    </div>
                    <div className="text-gray-600 text-sm font-semibold pt-8">
                      <Link
                        to="/devices?add=true"
                        className="px-8 py-4 font-bold text-white bg-blue-500 rounded hover:bg-blue-600"
                      >
                        Add a Device
                      </Link>
                    </div>
                  </td>
                </tr>
              </Fragment>
            )}
      </tbody>
    </table>
    </>
  );
};

export default DevicesTable;
